/**
 * The review queue for sellers' offers.
 *
 * A seller proposes a discount and who pays for it. A seller-funded offer
 * costs Terra Shoppy nothing; a 50-50 one is a promotion spend, paid to the
 * seller at settlement for every unit sold. Nothing changes a price until it is
 * approved here, and a rejection must say why — the seller reads it and can
 * fix and resend the offer.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgePercent, Check, Clock, X } from 'lucide-react';
import { useState } from 'react';

import { ApiClientError, type OfferPhase, type SellerOffer } from '@terra/api-client';
import type { OfferStatus } from '@terra/contracts';
import { Badge, type BadgeTone, EmptyState, PageHeader, Spinner, toast } from '@terra/ui';

import { api } from '../lib/api';

const rupees = (p: number) => `₹${(p / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const errMsg = (e: unknown, fallback: string) =>
  e instanceof ApiClientError ? e.message : fallback;
const when = (iso: string) =>
  new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });

const PHASE: Record<OfferPhase, { label: string; tone: BadgeTone }> = {
  PENDING_APPROVAL: { label: 'Waiting for review', tone: 'warning' },
  SCHEDULED: { label: 'Approved · scheduled', tone: 'info' },
  LIVE: { label: 'Live', tone: 'success' },
  ENDED: { label: 'Ended', tone: 'neutral' },
  REJECTED: { label: 'Rejected', tone: 'danger' },
  CANCELLED: { label: 'Cancelled by seller', tone: 'neutral' },
};

const TABS: Array<{ key: OfferStatus | 'ALL'; label: string }> = [
  { key: 'PENDING_APPROVAL', label: 'Waiting for review' },
  { key: 'APPROVED', label: 'Approved' },
  { key: 'REJECTED', label: 'Rejected' },
  { key: 'CANCELLED', label: 'Cancelled' },
  { key: 'ALL', label: 'All' },
];

function OfferReviewCard({ o }: { o: SellerOffer }) {
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const done = (msg: string) => {
    toast.success(msg);
    setNote('');
    setRejecting(false);
    void qc.invalidateQueries({ queryKey: ['admin-seller-offers'] });
  };
  const approve = useMutation({
    mutationFn: () => api.marketing.approveOffer(o.id, note.trim() || undefined),
    onSuccess: () => done('Offer approved — the seller has been told'),
    onError: (e) => toast.error(errMsg(e, 'Could not approve the offer')),
  });
  const reject = useMutation({
    mutationFn: () => api.marketing.rejectOffer(o.id, note.trim()),
    onSuccess: () => done('Offer rejected — the seller has been told why'),
    onError: (e) => toast.error(errMsg(e, 'Could not reject the offer')),
  });

  const phase = PHASE[o.phase];
  const totalPlatform = o.items.reduce((s, i) => s + i.platformFundsPaise, 0);
  const deepest = Math.max(
    0,
    ...o.items.map((i) => (i.pricePaise > 0 ? 1 - i.offerPricePaise / i.pricePaise : 0)),
  );
  const pending = o.status === 'PENDING_APPROVAL';

  return (
    <div className="border-border/60 bg-surface space-y-4 rounded-2xl border p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-ink text-[15px] font-extrabold">{o.title}</p>
          <p className="text-ink-muted mt-0.5 text-[12px]">
            {o.vendor?.storeName ?? 'Seller'} · sent {when(o.createdAt)}
          </p>
          <p className="text-ink-muted mt-1 flex items-center gap-1 text-[12px]">
            <Clock className="h-3.5 w-3.5" /> {when(o.startsAt)} → {when(o.endsAt)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={o.funding === 'SPLIT' ? 'gold' : 'neutral'}>
            {o.funding === 'SPLIT' ? '50-50 · Terra Shoppy pays half' : 'Seller pays all'}
          </Badge>
          <Badge tone={phase.tone} dot>
            {phase.label}
          </Badge>
        </div>
      </div>

      {o.funding === 'SPLIT' && (
        <p className="bg-gold-50 text-gold-900 rounded-lg p-3 text-[13px]">
          Terra Shoppy pays its share (last column) on <b>every unit sold</b>, added to the
          seller&apos;s payout — {rupees(totalPlatform)} for one of each product. Approve only if
          the promotion is worth it.
        </p>
      )}
      {deepest >= 0.5 && (
        <p className="bg-state-warning-bg text-state-warning rounded-lg p-3 text-[13px]">
          Deep discount: up to {Math.round(deepest * 100)}% off. Check the selling price was not
          raised just before this offer.
        </p>
      )}
      {o.sellerNote && (
        <p className="bg-surface-subtle text-ink rounded-lg p-3 text-[13px]">
          <b>Seller&apos;s note:</b> {o.sellerNote}
        </p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full min-w-[620px] text-left text-[13px]">
          <thead>
            <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
              <th className="py-2 pr-4">Product</th>
              <th className="py-2 pr-4">Discount</th>
              <th className="py-2 pr-4 text-right">Price → offer</th>
              <th className="py-2 pr-4 text-right">Seller pays</th>
              <th className="py-2 text-right">Terra Shoppy pays</th>
            </tr>
          </thead>
          <tbody>
            {o.items.map((i) => (
              <tr key={i.productId} className="border-border/40 border-b last:border-0">
                <td className="py-2 pr-4">
                  <div className="flex items-center gap-2">
                    {i.imageUrl ? (
                      <img src={i.imageUrl} alt="" className="h-9 w-9 rounded-md object-cover" />
                    ) : (
                      <div className="bg-surface-subtle h-9 w-9 rounded-md" />
                    )}
                    <span className="text-ink font-semibold">{i.title}</span>
                  </div>
                </td>
                <td className="py-2 pr-4">
                  {i.discountType === 'PERCENTAGE'
                    ? `${i.discountAmount / 100}% off`
                    : `${rupees(i.discountAmount)} off`}
                </td>
                <td className="py-2 pr-4 text-right">
                  <span className="text-ink-muted line-through">{rupees(i.pricePaise)}</span>{' '}
                  <b className="text-ink">{rupees(i.offerPricePaise)}</b>
                </td>
                <td className="py-2 pr-4 text-right">{rupees(i.sellerFundsPaise)}</td>
                <td className="py-2 text-right font-semibold">{rupees(i.platformFundsPaise)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {o.reviewNote && !pending && (
        <p className="text-ink-muted text-[12px]">
          Review note: <span className="text-ink">{o.reviewNote}</span>
        </p>
      )}

      {pending && (
        <div className="border-border/40 space-y-3 border-t pt-4">
          <textarea
            className="border-border w-full rounded-xl border p-3 text-[13px]"
            rows={2}
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={
              rejecting
                ? 'Why is it rejected? The seller reads this (required)'
                : 'Note for the seller (optional)'
            }
          />
          <div className="flex flex-wrap justify-end gap-2">
            {rejecting ? (
              <>
                <button
                  type="button"
                  onClick={() => setRejecting(false)}
                  className="border-border text-ink h-10 rounded-xl border px-4 text-[13px] font-bold"
                >
                  Back
                </button>
                <button
                  type="button"
                  disabled={note.trim().length < 3 || reject.isPending}
                  onClick={() => reject.mutate()}
                  className="bg-state-danger flex h-10 items-center gap-1 rounded-xl px-4 text-[13px] font-bold text-white disabled:opacity-50"
                >
                  <X className="h-4 w-4" /> {reject.isPending ? 'Rejecting…' : 'Confirm reject'}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setRejecting(true)}
                  className="border-border text-state-danger h-10 rounded-xl border px-4 text-[13px] font-bold"
                >
                  Reject
                </button>
                <button
                  type="button"
                  disabled={approve.isPending}
                  onClick={() => approve.mutate()}
                  className="bg-ink hover:bg-ink/90 flex h-10 items-center gap-1 rounded-xl px-4 text-[13px] font-bold text-white disabled:opacity-50"
                >
                  <Check className="h-4 w-4" /> {approve.isPending ? 'Approving…' : 'Approve'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function SellerOffersPage() {
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('PENDING_APPROVAL');
  const offers = useQuery({
    queryKey: ['admin-seller-offers', tab],
    queryFn: () => api.marketing.offersForReview(tab === 'ALL' ? undefined : { status: tab }),
  });
  const list = offers.data ?? [];

  return (
    <div className="space-y-6 pb-8">
      <PageHeader
        title="Seller offers"
        description="Discounts sellers want to run. Prices change only after you approve. 50-50 offers are paid half by Terra Shoppy."
      />

      <div className="border-border/60 flex gap-1 overflow-x-auto border-b">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`whitespace-nowrap border-b-2 px-3 py-2 text-[13px] font-bold ${tab === t.key ? 'border-ink text-ink' : 'text-ink-muted hover:text-ink border-transparent'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {offers.isLoading ? (
        <div className="grid min-h-[240px] place-items-center">
          <Spinner size="lg" />
        </div>
      ) : list.length === 0 ? (
        <EmptyState
          icon={<BadgePercent className="text-ink-muted h-10 w-10" />}
          title={tab === 'PENDING_APPROVAL' ? 'Nothing to review' : 'No offers here'}
          description="Offers sellers send from their dashboard appear here."
        />
      ) : (
        <div className="space-y-4">
          {list.map((o) => (
            <OfferReviewCard key={o.id} o={o} />
          ))}
        </div>
      )}
    </div>
  );
}
